import React, { useEffect, useState } from 'react';
import {
  required,
  isAmericanOrCanadianRoutingNumber,
  hasLength,
} from 'redux-freeform';
import {
  ButtonWithAction,
  Cluster,
  FormInput,
  Text,
  util,
  Box,
  RadioGroup,
} from '@thecb/components';

import { formatCurrencyInput } from '../../../utils/helpers';

import {
  checkReducer,
  checkMapStateToProps,
  checkMapDispatchToProps,
} from '../CheckoutForm.state';
import { useDispatch, useSelector } from 'react-redux';
import { initiateCheckScan } from '../Checkout.state';
import { PROCESSING_MODAL } from '../Checkout';
import {
  CHECK,
  CHECK_TYPE,
  COMPANY_NAME_FIELD,
} from '../../../utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { requireErrors, paymentAmountErrors } from './shared-constants';

const ACCOUNT_NUMBER_FIELD = 'account_number';
const CHECK_NUMBER_FIELD = 'check_number';
const ROUTING_NUMBER_FIELD = 'routing_number';

const routingNumberErrorMessages = {
  [required.error]: 'Routing Number is required',
  [isAmericanOrCanadianRoutingNumber.error]: 'Routing Number format is invalid',
};

const checkNumberErrorMessages = {
  [required.error]: 'Check Number is required',
  [hasLength.error]: 'Check Number should be between 3 and 15 characters',
};

const checkSubmissionTypeRadioGroupConfig = [
  { id: 'check-reader', value: 'Check reader', labelText: 'Check Reader' },
  { id: 'manual-entry', value: 'Manual entry', labelText: 'Manual Entry' },
];

const accountTypeRadioGroupConfig = [
  { id: 'checking', value: 'checking', labelText: 'Checking' },
  { id: 'savings', value: 'savings', labelText: 'Savings' },
];

const checkTypeRadioGroupConfig = [
  {
    id: CHECK_TYPE.PERSONAL,
    value: CHECK_TYPE.PERSONAL,
    labelText: 'Personal Check',
  },
  {
    id: CHECK_TYPE.BUSINESS,
    value: CHECK_TYPE.BUSINESS,
    labelText: 'Business Check',
  },
  {
    id: CHECK_TYPE.MONEY_ORDER,
    value: CHECK_TYPE.MONEY_ORDER,
    labelText: 'Money Order',
  },
  {
    id: CHECK_TYPE.CASHIERS_CHECK,
    value: CHECK_TYPE.CASHIERS_CHECK,
    labelText: "Cashier's Check",
  },
];

let CheckCheckoutForm = ({
  actions,
  fields,
  openProcessingModal,
  makePayment,
  remainingBalance,
  processBtnText,
  splitTenderAllowed,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isCheckProcessing, isPollingForCheckResponse } =
    useSelector((state) => state.checkout);
  const [errors, handleErrors] = useState(false);
  const isManualEntrySelected =
    fields.submission_type.rawValue === 'Manual entry';
  const isPersonalCheck = fields.check_type.rawValue === CHECK_TYPE.PERSONAL;
  const isBusinessCheck = fields.check_type.rawValue === CHECK_TYPE.BUSINESS;

  useEffect(() => {
    if (remainingBalance) {
      actions.fields.amount.set(formatCurrencyInput(remainingBalance));
    }
  }, [remainingBalance]);

  const handleCheckSubmit = util.general.generateClickHandler(
    { fields },
    () => handleErrors(true),
    () => {
      const payload = {
        amount_given: Number(fields.amount.rawValue),
        first_name: fields.first_name.rawValue,
        last_name: fields.last_name.rawValue,
        check_type: fields.check_type.rawValue,
        submission_type: fields.submission_type.rawValue,
        payment_tender_type: CHECK,
        account_type: fields.account_type.rawValue,
        ...(isBusinessCheck && {
          company_name: fields.company_name.rawValue,
        }),
        ...(isManualEntrySelected && {
          check_number: fields.check_number.rawValue,
          routing_number: fields.routing_number.rawValue,
          account_number: fields.account_number.rawValue,
          isManualEntry: true,
        }),
      };
      if (isManualEntrySelected) {
        openProcessingModal(PROCESSING_MODAL);
        makePayment({ ...payload, navigate, location });
      } else {
        dispatch(initiateCheckScan({ ...payload, navigate, location }));
      }
    },
  );

  const resetFieldValueIfDirty = (field) => {
    if (fields && fields[field]?.dirty) {
      actions.fields[field].set('');
    }
  };

  return (
    <Box padding="21px" as="form" onSubmit={handleCheckSubmit}>
      <FormInput
        disabled={!splitTenderAllowed}
        id="check-payment-amt"
        type="number"
        labelTextWhenNoError="Payment Amount"
        errorMessages={paymentAmountErrors}
        field={fields.amount}
        errors={fields.errors}
        fieldActions={actions.fields.amount}
        labelTextVariant="p"
        errorTextVariant="p"
      />
      <FormInput
        labelTextWhenNoError="First Name"
        errorMessages={requireErrors('First Name is required')}
        field={fields.first_name}
        showErrors={errors}
        fieldActions={actions.fields.first_name}
        labelTextVariant="p"
        errorTextVariant="p"
      />
      <FormInput
        labelTextWhenNoError="Last Name"
        errorMessages={requireErrors('Last Name is required')}
        field={fields.last_name}
        showErrors={errors}
        fieldActions={actions.fields.last_name}
        labelTextVariant="p"
        errorTextVariant="p"
      />
      <Text fontSize={'1.25em'}>Check Information</Text>
      <RadioGroup
        field={fields.submission_type}
        fieldActions={actions.fields.submission_type}
        groupName="submission-type"
        headingText="Submission Type"
        config={checkSubmissionTypeRadioGroupConfig}
        extraStyles={`margin-bottom: 8px;`}
        headingFontSize="1rem"
      />
      <RadioGroup
        handleChange={(e) => {
          if (e.currentTarget.getAttribute('name')) {
            // clear account/routing number fields on check type change
            resetFieldValueIfDirty(ACCOUNT_NUMBER_FIELD);
            resetFieldValueIfDirty(CHECK_NUMBER_FIELD);
            resetFieldValueIfDirty(ROUTING_NUMBER_FIELD);
            resetFieldValueIfDirty(COMPANY_NAME_FIELD);
          }
        }}
        field={fields.check_type}
        fieldActions={actions.fields.check_type}
        groupName="check-type"
        headingText="Check Type"
        config={checkTypeRadioGroupConfig}
        extraStyles={`margin-bottom: 8px;`}
        headingFontSize="1rem"
      />
      <Box padding="16px 0 0">
        {isBusinessCheck && (
          <FormInput
            labelTextWhenNoError="Business Name"
            field={fields.company_name}
            errors={fields.errors}
            showErrors={errors}
            fieldActions={actions.fields.company_name}
            labelTextVariant="p"
            errorTextVariant="p"
          />
        )}
        {isManualEntrySelected && (
          <>
            {isPersonalCheck && (
              <RadioGroup
                field={fields.account_type}
                fieldActions={actions.fields.account_type}
                groupName="account-type"
                headingText="Account Type"
                config={accountTypeRadioGroupConfig}
                extraStyles={`margin-bottom: 8px;`}
                headingFontSize="1rem"
              />
            )}
            <FormInput
              labelTextWhenNoError="Check Number"
              errorMessages={checkNumberErrorMessages}
              field={fields.check_number}
              errors={fields.errors}
              showErrors={errors}
              fieldActions={actions.fields.check_number}
              labelTextVariant="p"
              errorTextVariant="p"
            />
            <FormInput
              labelTextWhenNoError="Routing Number"
              errorMessages={routingNumberErrorMessages}
              field={fields.routing_number}
              errors={fields.errors}
              showErrors={errors}
              fieldActions={actions.fields.routing_number}
              labelTextVariant="p"
              errorTextVariant="p"
            />
            <FormInput
              labelTextWhenNoError="Account Number"
              errorMessages={requireErrors('Account Number is required')}
              field={fields.account_number}
              errors={fields.errors}
              showErrors={errors}
              fieldActions={actions.fields.account_number}
              labelTextVariant="p"
              errorTextVariant="p"
            />
          </>
        )}
        <Cluster>
          <ButtonWithAction
            type="reset"
            text={'Back to Cart'}
            action={() => navigate('/pos/cart')}
            variant="secondary"
            dataQa="view-cart"
            extraStyles="margin-left: 0"
            textExtraStyles="font-size: 1rem;"
          />
          <ButtonWithAction
            type="submit"
            id="check-payment-btn"
            textExtraStyles="font-size: 1rem;"
            disabled={
              isLoading ||
              isPollingForCheckResponse ||
              isCheckProcessing ||
              fields.amount.rawValue <= 0 ||
              fields.amount.hasErrors
            }
            isLoading={
              isLoading || isPollingForCheckResponse || isCheckProcessing
            }
            text={processBtnText}
            action={handleCheckSubmit}
          />
        </Cluster>
      </Box>
    </Box>
  );
};

CheckCheckoutForm.reducer = checkReducer;
CheckCheckoutForm.mapStateToProps = checkMapStateToProps;
CheckCheckoutForm.mapDispatchToProps = checkMapDispatchToProps;

export default CheckCheckoutForm;
